import { useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, redirect, useParams, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usePageBar } from "../../components/shell/page-bar";
import { canSeeAccommodation, canSeeBuildings, canSeeHelpdesk, canSeeHostAccommodation, canUseDesk, accommodationView } from "../../lib/accommodation/access";
import { accommodationSummaryOptions } from "../../lib/api/accommodation";
import { useDefaultGamesId } from "../../lib/competition/games";

/**
 * Accommodation (M11, PLAN §4) — the layout: her tab row over the page.
 *   host      Overview · Rooming · Buildings · Check-in & out · Helpdesk
 *   hotel     Overview (S5's hotel view) · Buildings (its own, read-only) · Helpdesk
 *   NOC       one page (S5's rooming view), no tabs
 * A contingent (`rooming/$orgId`) and a building (`buildings/$buildingId`)
 * are full pages with the back arrow instead of the tabs. Helpdesk
 * (`accommodation.helpdesk.tsx`) is S5's.
 *
 * Who: `canSeeAccommodation` — a courtesy, not the control: the API scopes
 * every row in SQL and checks every write.
 */
export const Route = createFileRoute("/_app/accommodation")({
	validateSearch: z.object({ games_id: z.string().uuid().optional().catch(undefined) }),
	beforeLoad: ({ context }) => {
		if (!canSeeAccommodation(context.session)) throw redirect({ to: "/" });
	},
	staticData: { contentTop: "tabbed" },
	component: AccommodationLayout,
});

type TabTo = "/accommodation" | "/accommodation/rooming" | "/accommodation/buildings" | "/accommodation/check-in" | "/accommodation/helpdesk";
type Tab = { key: "overview" | "rooming" | "buildings" | "checkin" | "helpdesk"; to: TabTo; badge?: number; badgeHint?: string };

function AccommodationLayout() {
	const { t } = useTranslation("accommodation");
	const { t: tn } = useTranslation("nav");
	const { formatNumber: fmt } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const params = useParams({ strict: false }) as { orgId?: string; buildingId?: string };
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const view = accommodationView(session);
	const host = canSeeHostAccommodation(session);
	const gamesId = useDefaultGamesId(search.games_id);
	// Her tab badges: people without a bed, check-ins still to do today, open tickets — the Overview's own query on today.
	const summary = useQuery({ ...accommodationSummaryOptions(gamesId ?? ""), enabled: !!gamesId && host });
	const s = summary.data;
	const openTickets = s ? s.open_tickets.reduce((n, x) => n + x.count, 0) : undefined;
	const detail = params.orgId !== undefined || params.buildingId !== undefined;

	// Her heading in either menu: "Accommodation / Logistics & Hospitality".
	usePageBar({ subtitle: tn("sections.logistics") });

	if (detail) return <Outlet />;

	const tabs: Tab[] = [];
	if (view === "host" || view === "hotel") tabs.push({ key: "overview", to: "/accommodation" });
	if (host)
		tabs.push({
			key: "rooming",
			to: "/accommodation/rooming",
			badge: s?.without_bed ?? undefined,
			badgeHint: s?.without_bed != null ? t("tabs.withoutBed", { count: s.without_bed, n: fmt(s.without_bed) }) : undefined,
		});
	if (canSeeBuildings(session)) tabs.push({ key: "buildings", to: "/accommodation/buildings" });
	if (host && canUseDesk(session))
		tabs.push({
			key: "checkin",
			to: "/accommodation/check-in",
			badge: s?.arriving,
			badgeHint: s ? t("tabs.toCheckIn", { count: s.arriving, n: fmt(s.arriving) }) : undefined,
		});
	if (view !== "noc" && canSeeHelpdesk(session))
		tabs.push({
			key: "helpdesk",
			to: "/accommodation/helpdesk",
			badge: openTickets,
			badgeHint: openTickets !== undefined ? t("tabs.openTickets", { count: openTickets, n: fmt(openTickets) }) : undefined,
		});

	const current = (to: TabTo) => (to === "/accommodation" ? pathname === to || pathname === `${to}/` : pathname.startsWith(to));

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{tabs.length > 1 && (
				<nav aria-label={t("tabs.label")} className="-mt-[0.25rem] flex gap-[0.25rem] overflow-x-auto border-b border-gms-line [scrollbar-width:none]">
					{tabs.map((tab) => (
						<Link
							key={tab.key}
							to={tab.to}
							search={(p: { games_id?: string }) => ({ games_id: p.games_id })}
							aria-current={current(tab.to) ? "page" : undefined}
							className={`relative inline-flex h-[2.75rem] shrink-0 items-center gap-[0.375rem] px-[0.75rem] text-[0.875rem] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
								current(tab.to)
									? "font-semibold text-gms-navy after:absolute after:inset-x-[0.5rem] after:-bottom-px after:h-[3px] after:rounded-t-[3px] after:bg-gms-red"
									: "font-medium text-gms-ink-2 hover:text-gms-navy"
							}`}
						>
							{t(`tabs.${tab.key}`)}
							{!!tab.badge && tab.badge > 0 && (
								<span
									title={tab.badgeHint}
									className="inline-grid h-[1.125rem] min-w-[1.25rem] place-items-center rounded-full bg-gms-red px-[0.3125rem] text-[0.6875rem] font-bold text-white tabular-nums"
								>
									{fmt(tab.badge)}
								</span>
							)}
						</Link>
					))}
				</nav>
			)}
			<Outlet />
		</div>
	);
}
